import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { submitLead } from "../lib/api";
import { trackEnquiry } from "../lib/analytics";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December", "Not decided",
];

const BUDGETS = [
  "Under ₹1 lakh per person",
  "₹1–2.5 lakh per person",
  "₹2.5–5 lakh per person",
  "₹5–10 lakh per person",
  "Above ₹10 lakh per person",
  "Advise me",
];

export default function InquiryForm({
  source = "contact",
  sourceRef = "",
  sourceTitle = "",
  destination = "",
  variant = "card",
  corporate = false,
  title = "Plan this journey",
  subtitle = "A travel designer replies within one business day.",
  itineraryId = null,
  compact = false,
}) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", message: "",
    travel_month: "", travellers: "2", duration: "", budget: "",
    company: "", designation: "", gstin: "", team_size: "", requirement: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please share your name, email and phone so we can reach you.");
      return;
    }
    setBusy(true);
    try {
      const res = await submitLead({
        ...form,
        travellers: Number(form.travellers) || null,
        source: corporate ? "corporate" : source,
        source_ref: sourceRef,
        source_title: sourceTitle,
        destination: destination || sourceTitle,
        itinerary_id: itineraryId,
        interests: [],
      });
      setDone(res);
      trackEnquiry({
        source,
        corporate,
        destination: destination || sourceTitle,
        code: res.code,
        hasItinerary: Boolean(itineraryId),
      });
      toast.success("Enquiry received", { description: `Reference ${res.code}` });
    } catch (err) {
      toast.error("We could not send that", {
        description:
          err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Please check your details and try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  const shell =
    variant === "card"
      ? "rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-6 sm:p-7"
      : "";

  if (done) {
    return (
      <div className={shell} data-testid="inquiry-success">
        <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[rgba(31,111,91,0.12)]">
          <Check className="h-5 w-5 text-[#1F6F5B]" />
        </div>
        <h3 className="font-display text-2xl text-[var(--ink)]">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-sm leading-relaxed text-[var(--muted-ink)]">{done.message}</p>
        <p className="mt-4 text-xs text-[var(--muted-ink)]">
          Your reference is{" "}
          <span data-testid="inquiry-reference" className="font-semibold text-[var(--brass-2)]">
            {done.code}
          </span>
          . Keep it handy when you speak to us.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={shell} data-testid="inquiry-form" noValidate>
      {title && (
        <div className="mb-5">
          <h3 className="font-display text-2xl leading-snug text-[var(--ink)]">{title}</h3>
          {subtitle && <p className="mt-1.5 text-sm text-[var(--muted-ink)]">{subtitle}</p>}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full name" required>
          <Input value={form.name} onChange={set("name")} placeholder="Your name"
            data-testid="inquiry-name-input" />
        </Field>
        <Field label="Email" required>
          <Input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com"
            data-testid="inquiry-email-input" />
        </Field>
        <Field label="Phone" required>
          <Input value={form.phone} onChange={set("phone")} placeholder="+91 98107 01234"
            data-testid="inquiry-phone-input" />
        </Field>
        <Field label={corporate ? "Company" : "City"}>
          <Input
            value={corporate ? form.company : form.city}
            onChange={corporate ? set("company") : set("city")}
            placeholder={corporate ? "Company name" : "Your city"}
            data-testid={corporate ? "inquiry-company-input" : "inquiry-city-input"}
          />
        </Field>

        {corporate ? (
          <>
            <Field label="Designation">
              <Input value={form.designation} onChange={set("designation")} placeholder="Head of People"
                data-testid="inquiry-designation-input" />
            </Field>
            <Field label="GSTIN">
              <Input value={form.gstin} onChange={set("gstin")} placeholder="For GST-compliant invoicing"
                data-testid="inquiry-gstin-input" />
            </Field>
            <Field label="Approximate group size">
              <Input value={form.team_size} onChange={set("team_size")} placeholder="e.g. 60 delegates"
                data-testid="inquiry-teamsize-input" />
            </Field>
            <Field label="Requirement">
              <Select value={form.requirement} onValueChange={set("requirement")}>
                <SelectTrigger data-testid="inquiry-requirement-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {["Managed business travel", "Offsite / retreat", "MICE / conference",
                    "Group booking", "Business visas", "Forex & insurance", "Destination wedding"].map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </>
        ) : (
          !compact && (
            <>
              <Field label="Travel month">
                <Select value={form.travel_month} onValueChange={set("travel_month")}>
                  <SelectTrigger data-testid="inquiry-month-select">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Travellers">
                <Input type="number" min="1" value={form.travellers} onChange={set("travellers")}
                  data-testid="inquiry-travellers-input" />
              </Field>
              <Field label="Nights">
                <Input value={form.duration} onChange={set("duration")} placeholder="e.g. 8 nights"
                  data-testid="inquiry-duration-input" />
              </Field>
              <Field label="Indicative budget">
                <Select value={form.budget} onValueChange={set("budget")}>
                  <SelectTrigger data-testid="inquiry-budget-select">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUDGETS.map((b) => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </>
          )
        )}
      </div>

      <div className="mt-4">
        <Field label={corporate ? "Tell us about the programme" : "Anything we should know?"}>
          <Textarea
            rows={3}
            value={form.message}
            onChange={set("message")}
            placeholder={
              corporate
                ? "Dates, cities, delegate mix, approval process…"
                : "Occasion, dietary needs, pace, anything that has disappointed you before…"
            }
            data-testid="inquiry-message-input"
          />
        </Field>
      </div>

      <Button
        type="submit"
        disabled={busy}
        data-testid="inquiry-submit-button"
        className="mt-5 h-12 w-full rounded-xl bg-[var(--brass)] text-sm font-semibold text-white hover:bg-[var(--brass-2)] active:scale-[0.99]"
      >
        {busy && (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending
          </>
        )}
        {!busy && (corporate ? "Request a corporate proposal" : "Send enquiry")}
      </Button>
      <p className="mt-3 text-center text-[0.7rem] leading-relaxed text-[var(--muted-ink)]">
        No spam, no call centres. Your details stay with your travel designer.
      </p>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs font-semibold text-[var(--ink-2)]">
        {label}
        {required && <span className="ml-0.5 text-[var(--brass)]">*</span>}
      </Label>
      {children}
    </div>
  );
}
